import { Navigate, useSearchParams } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';
import { OnboardingStage } from '@ndvr/customer-api';

import { cn } from '@/utils/cn';
import { useRequiredParams } from 'utils';
import { useAssignedFlags } from 'views/auth/auth-queries';
import { PageSpinner } from 'widgets';

import { AccountsView } from './AccountsView';
import { AgreementsView } from './AgreementsView';
import { ClientsView } from './ClientsView';
import { FidelityApplicationView } from './FidelityApplicationView';
import { NativeSelect } from './fields';
import { STAGES } from './onboarding-model';
import { useChangeOnboardingStage, useOnboardingStatus } from './onboarding-tab-queries';
import { useOnboardingHousehold } from './useOnboardingHousehold';

const VIEWS = [
    { value: 'clients', label: 'Clients' },
    { value: 'accounts', label: 'Accounts' },
    { value: 'agreements', label: 'Agreements' },
    { value: 'fidelity-application', label: 'Fidelity Application' },
] as const;
type View = (typeof VIEWS)[number]['value'];

/** Behind the ONBOARDING flag: without it, a link to the tab lands on the household's default tab instead. */
export const OnboardingTab = () => {
    const { data: flags, isPending } = useAssignedFlags();
    if (isPending) return <PageSpinner />;
    if (!flags?.includes(FeatureFlag.ONBOARDING)) return <Navigate to=".." replace />;
    return <OnboardingTabContent />;
};

const OnboardingTabContent = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const [searchParams, setSearchParams] = useSearchParams();
    const view: View = VIEWS.find((v) => v.value === searchParams.get('view'))?.value ?? 'clients';
    const household = useOnboardingHousehold(globalAccountGUID);

    const selectView = (next: View) =>
        setSearchParams(
            (params) => {
                params.set('view', next);
                return params;
            },
            { replace: true },
        );

    return (
        <div className="flex flex-col gap-7">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Onboarding">
                    {VIEWS.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            role="tab"
                            aria-selected={view === item.value}
                            onClick={() => selectView(item.value)}
                            className={cn(
                                'cursor-pointer rounded-full px-4 py-1.5 text-sm transition-colors',
                                view === item.value
                                    ? 'bg-seafoam text-cocoa-900 font-medium'
                                    : 'text-cocoa-600 hover:bg-sand-100',
                            )}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                <StageSelect householdGUID={globalAccountGUID} />
            </div>
            {household.isLoading ? (
                <div className="flex justify-center p-8">
                    <PageSpinner />
                </div>
            ) : household.isError ? (
                <p role="alert">Could not load the household.</p>
            ) : (
                <>
                    {view === 'agreements' && (
                        <AgreementsView householdGUID={globalAccountGUID} household={household} />
                    )}
                    {view === 'clients' && <ClientsView householdGUID={globalAccountGUID} household={household} />}
                    {view === 'accounts' && <AccountsView householdGUID={globalAccountGUID} household={household} />}
                    {view === 'fidelity-application' && <FidelityApplicationView household={household} />}
                </>
            )}
        </div>
    );
};

/** What the client sees in the portal follows this; the backend only moves forward, except back to the client. */
const StageSelect = ({ householdGUID }: { householdGUID: string }) => {
    const { data: status, isError } = useOnboardingStatus(householdGUID);
    const { mutate, isPending } = useChangeOnboardingStage(householdGUID);
    if (isError) return <span className="text-cocoa-500 text-sm">No onboarding stage</span>;
    return (
        <label className="flex items-center gap-3 text-sm">
            <span className="text-cocoa-600 font-medium">Stage</span>
            <NativeSelect
                aria-label="Stage"
                fit
                className="border-cocoa-900 font-medium"
                value={status?.stage ?? ''}
                disabled={!status || isPending}
                onChange={(stage) => mutate(stage as OnboardingStage)}
                options={STAGES}
            />
        </label>
    );
};
